import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { StripArrow, scrollStrip } from "@/components/StripArrow";
import { useVideos } from "@/components/HeroVideo";

const Tile = ({ v, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    data-testid={`carousel-video-tile-${v.id}`}
    className="group relative w-72 sm:w-96 aspect-video flex-shrink-0 mx-2 snap-center rounded-xl overflow-hidden bg-[#1E3048] border border-[#8FA58A]/30 shadow-lg transition-transform duration-300 hover:scale-[1.03] hover:border-[#E2705A]"
  >
    <img src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title || "Video"} loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
    <span className="absolute inset-0 flex items-center justify-center">
      <span className="w-12 h-12 rounded-full bg-[#E2705A] text-[#FDFBF7] flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform"><Play className="w-5 h-5 ml-0.5" fill="currentColor" /></span>
    </span>
    {v.title && <span className="absolute bottom-0 inset-x-0 px-3 py-2 text-left text-xs font-semibold text-[#FDFBF7] bg-gradient-to-t from-black/80 to-transparent">{v.title}</span>}
  </button>
);

export const LandscapeCarousel = () => {
  const { data = [] } = useVideos();
  const [idx, setIdx] = useState(null);
  const trackRef = useRef(null);
  const videos = data.filter((v) => v.type === "carousel");
  if (!videos.length) return null;
  const open = idx !== null ? videos[idx] : null;
  const step = (d) => setIdx((i) => (i + d + videos.length) % videos.length);
  return (
    <section className="py-12 bg-[#1B2A41] relative" data-testid="landscape-carousel">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 mb-4 flex items-baseline justify-between">
        <h2 className="font-type font-bold text-base md:text-lg text-[#E2705A] uppercase tracking-wider">Watch</h2>
        <span className="text-xs text-[#8FA58A] font-type">{videos.length} video{videos.length === 1 ? "" : "s"} · swipe or use the arrows</span>
      </div>
      <div className="relative">
        <StripArrow dir={-1} onClick={() => scrollStrip(trackRef, -1)} testId="carousel-strip-prev" label="Previous videos" className="left-1 sm:left-3" />
        <StripArrow dir={1} onClick={() => scrollStrip(trackRef, 1)} testId="carousel-strip-next" label="Next videos" className="right-1 sm:right-3" />
        <div ref={trackRef} data-testid="carousel-strip" className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth px-11 sm:px-14 py-2 no-scrollbar">
          {videos.map((v, i) => <Tile key={v.id} v={v} onOpen={() => setIdx(i)} />)}
        </div>
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setIdx(null)}>
        <DialogContent data-testid="carousel-video-player" className="p-0 bg-black border-[#E2705A]/40 max-w-[min(94vw,960px)] overflow-visible [&>button]:z-20">
          <DialogTitle className="sr-only">{open?.title || "Video"}</DialogTitle>
          {open && (
            <>
              <div className="aspect-video w-full rounded-lg overflow-hidden">
                <iframe key={open.id} className="w-full h-full" src={`https://www.youtube.com/embed/${open.youtube_id}?autoplay=1&rel=0`} title={open.title || "Video"} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
              {videos.length > 1 && (
                <>
                  <StripArrow dir={-1} onClick={() => step(-1)} testId="carousel-player-prev" label="Previous video" className="-left-12 hidden sm:flex" />
                  <StripArrow dir={1} onClick={() => step(1)} testId="carousel-player-next" label="Next video" className="-right-12 hidden sm:flex" />
                  <div className="flex items-center justify-between px-3 py-2 text-xs text-[#FDFBF7]/80 font-type">
                    <button type="button" onClick={() => step(-1)} className="sm:hidden inline-flex items-center gap-1 text-[#E2705A]"><ChevronLeft className="w-4 h-4" /> Prev</button>
                    <span data-testid="carousel-player-counter">{idx + 1} / {videos.length}</span>
                    <button type="button" onClick={() => step(1)} className="sm:hidden inline-flex items-center gap-1 text-[#E2705A]">Next <ChevronRight className="w-4 h-4" /></button>
                  </div>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
